import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
	Icon, Typography
} from '@material-ui/core';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import * as ActionsDialog from 'app/store/actions';
import { getErrorReportingSettings, sendErrorReport } from 'app/main/settings/store/actions/general.actions';
import Logs from './Logs';
import { getLogs } from './store/actions';

const StackErrorDialog = ({ error, stackId }) => {
	const dispatch = useDispatch();
	const logs = useSelector(({ stacks }) => stacks.stack ? stacks.stack.logs : null);
	const isWarning = error.instructions?.severity === 'warning';

	const [errorReportingEnabled, setErrorReportingEnabled] = useState(false);
	const [reportSent, setReportSent] = useState(false);
	const [reportSending, setReportSending] = useState(false);
	const [showReportPreview, setShowReportPreview] = useState(false);

	useEffect(() => {
		dispatch(getErrorReportingSettings()).then(data => {
			if (data) setErrorReportingEnabled(data.enabled || false);
		});
	}, [dispatch]);

	const buildReportPayload = () => ({
		event_type: 'error.stack_build',
		message: 'Stack build failed - ' + (error.instructions?.title || error.type || 'unknown'),
		details: {
			heidi_version: process.env.REACT_APP_VERSION || 'unknown',
			stack_id_hash: stackId ? btoa(stackId).slice(0, 16) : 'unknown',
			error_type: error.type || 'unknown',
			error_message: error.instructions?.message || '',
			failed_component: error.instructions?.details?.failedComponent || '',
			error_codes: error.instructions?.details?.errorCodes || [],
			timestamp: new Date().toISOString(),
		},
	});

	const handleSendReport = async () => {
		setReportSending(true);
		try {
			const payload = buildReportPayload();
			await dispatch(sendErrorReport(payload.event_type, payload.message, payload.details));
			setReportSent(true);
			setShowReportPreview(false);
		} finally {
			setReportSending(false);
		}
	};

	const handleViewLogs = () => {
		dispatch(ActionsDialog.closeDialog());
		const type = 'build';
		dispatch(getLogs({ id: stackId, type })).then(() => {
			axios.get(`${API_BASE_URL}/api/stacks/${stackId}`, { headers: { 'x-auth-token': localStorage.getItem('jwt_access_token') } })
				.then(res => {
					dispatch(ActionsDialog.openDialog({
						children: <Logs stack={res.data} type={type} />,
						maxWidth: 'md',
						fullWidth: true,
						onClose: (event, reason) => {
							if (reason !== 'backdropClick') dispatch(ActionsDialog.closeDialog());
						}
					}));
				});
		});
	};

	return (
		<React.Fragment>
			<DialogTitle id="alert-dialog-title">
				<div className="flex items-center">
					<Icon style={{ color: isWarning ? '#ff9800' : '#f44336', fontSize: '28px', marginRight: '12px' }}>
						{isWarning ? 'warning' : 'error_outline'}
					</Icon>
					<Typography variant="h6" component="span" className="font-bold">
						{error.instructions?.title || 'Stack Build Error'}
					</Typography>
				</div>
			</DialogTitle>
			<DialogContent className="p-24">
				{/* Error Type Badge */}
				<div style={{
					backgroundColor: isWarning ? 'rgba(255, 152, 0, 0.1)' : 'rgba(244, 67, 54, 0.1)',
					borderRadius: '4px',
					padding: '6px 12px',
					display: 'inline-block',
					marginBottom: '16px'
				}}>
					<Typography variant="caption" style={{ color: isWarning ? '#ff9800' : '#f44336', fontWeight: 'bold', fontSize: '12px' }}>
						{error.type}
					</Typography>
				</div>

				{/* Main Message */}
				{error.instructions?.message && (
					<Typography variant="body1" paragraph style={{ marginBottom: '24px' }}>
						{error.instructions.message}
					</Typography>
				)}

				{/* CLI Remediation */}
				{error.instructions?.remediation?.cli?.description && (
					<div style={{ marginBottom: '24px' }}>
						<Typography variant="body2" color="textSecondary" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
							✓ Solution - {error.instructions.remediation.cli.description}
						</Typography>
						{error.instructions.remediation.cli.command && (
							<div style={{ marginTop: '12px' }}>
								<pre style={{
									backgroundColor: '#263238', color: '#aed581', padding: '16px',
									borderRadius: '4px', overflow: 'auto', fontSize: '13px',
									fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace',
									border: '1px solid rgba(255, 255, 255, 0.1)', margin: 0
								}}>
									<code>{error.instructions.remediation.cli.command}</code>
								</pre>
								<Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '8px', fontStyle: 'italic' }}>
									💡 Copy and run this command in Azure CLI
								</Typography>
							</div>
						)}
					</div>
				)}

				{/* Portal Link */}
				{error.instructions?.remediation?.portal?.url && (
					<div style={{
						marginBottom: '24px', padding: '12px 16px',
						backgroundColor: 'rgba(33, 150, 243, 0.08)',
						borderRadius: '4px', borderLeft: '4px solid #2196f3'
					}}>
						<Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
							Alternative: Use Azure Portal
						</Typography>
						<a href={error.instructions.remediation.portal.url} target="_blank" rel="noopener noreferrer"
							style={{ color: '#2196f3', textDecoration: 'none', display: 'flex', alignItems: 'center', fontWeight: 500 }}>
							<Icon style={{ fontSize: '18px', marginRight: '6px' }}>open_in_new</Icon>
							{error.instructions.remediation.portal.description || 'Open in Azure Portal'}
						</a>
						{error.instructions.remediation.portal.note && (
							<Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '8px' }}>
								{error.instructions.remediation.portal.note}
							</Typography>
						)}
					</div>
				)}

				{/* Next Steps */}
				{error.instructions?.nextSteps && error.instructions.nextSteps.length > 0 && (
					<div style={{ marginBottom: '16px' }}>
						<Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '12px' }}>
							📋 Next Steps:
						</Typography>
						<ol style={{ paddingLeft: '24px', margin: 0 }}>
							{error.instructions.nextSteps.map((step, idx) => (
								<li key={idx} style={{ marginBottom: '8px' }}>
									<Typography variant="body2" color="textSecondary">{step}</Typography>
								</li>
							))}
						</ol>
					</div>
				)}

				{/* Generic Error Details (for UNKNOWN errors) */}
				{error.type === 'UNKNOWN' && error.instructions?.details && (
					<div style={{ marginBottom: '16px' }}>
						{error.instructions.details.failedComponent && (
							<div style={{ marginBottom: '12px' }}>
								<Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>Failed Component:</Typography>
								<Typography variant="body2" color="textSecondary">{error.instructions.details.failedComponent}</Typography>
							</div>
						)}
						{error.instructions.details.errorCodes && error.instructions.details.errorCodes.length > 0 && (
							<div style={{ marginBottom: '12px' }}>
								<Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>Error Codes:</Typography>
								<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
									{error.instructions.details.errorCodes.map((code, idx) => (
										<span key={idx} style={{
											backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#f44336',
											padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace'
										}}>
											{code}
										</span>
									))}
								</div>
							</div>
						)}
						{error.instructions.details.errorMessages && error.instructions.details.errorMessages.length > 0 && (
							<div style={{ marginBottom: '12px' }}>
								<Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Error Messages:</Typography>
								<div style={{ backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800', padding: '12px', borderRadius: '4px' }}>
									{error.instructions.details.errorMessages.map((msg, idx) => (
										<Typography key={idx} variant="body2" style={{
											marginBottom: idx < error.instructions.details.errorMessages.length - 1 ? '8px' : 0,
											fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace', fontSize: '12px'
										}}>
											{msg}
										</Typography>
									))}
								</div>
							</div>
						)}
					</div>
				)}

				{/* Timestamp */}
				{error.timestamp && (
					<Typography variant="caption" color="textSecondary" style={{
						display: 'block', marginTop: '24px', paddingTop: '16px',
						borderTop: '1px solid rgba(0, 0, 0, 0.12)', fontSize: '11px'
					}}>
						⏰ Error occurred at: {new Date(error.timestamp).toLocaleString()}
					</Typography>
				)}
			</DialogContent>
			<DialogActions style={{ padding: '16px 24px' }}>
				<Button onClick={handleViewLogs} color="default" variant="outlined" startIcon={<Icon>description</Icon>}>
					View Logs
				</Button>
				{!reportSent && (
					<Button
						color="secondary"
						variant="outlined"
						onClick={() => {
							if (errorReportingEnabled) {
								handleSendReport();
							} else {
								setShowReportPreview(true);
							}
						}}
						disabled={reportSending}
					>
						{reportSending ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
						Send Error Report
					</Button>
				)}
				{reportSent && (
					<Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
						Report sent.
					</Typography>
				)}
				<Button onClick={() => dispatch(ActionsDialog.closeDialog())} color="primary" variant="contained" autoFocus>
					Close
				</Button>
			</DialogActions>

			{/* Error Report Preview Dialog */}
			<Dialog open={showReportPreview} onClose={() => setShowReportPreview(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Send Error Report</DialogTitle>
				<DialogContent>
					<Typography variant="body2" style={{ marginBottom: 12 }}>
						This will send an anonymous error report to Heidi support containing:
					</Typography>
					<Typography variant="body2" component="ul" style={{ paddingLeft: 20, marginBottom: 12 }}>
						<li>Error type and message</li>
						<li>Heidi version</li>
						<li>Failed component and error codes</li>
						<li>Hashed stack ID (not reversible)</li>
					</Typography>
					<Typography variant="caption" color="textSecondary">
						No IP addresses, credentials, or personal data are included. You can enable automatic error reporting in Settings &gt; General.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowReportPreview(false)}>Cancel</Button>
					<Button onClick={handleSendReport} color="primary" disabled={reportSending}>
						{reportSending ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
						Send Report
					</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
};

export default StackErrorDialog;
